import { forwardRef } from 'react';
import { breakpoints, Heading, Inline, Stack, Text, vars } from '@etg/wings';
import { ReactComponent as RevolutCheckIcon } from '@eti/assets/icons/revolut/check.svg';
import { ReactComponent as RevolutErrorIcon } from '@eti/assets/icons/revolut/error.svg';
import { ReactComponent as RevolutWarningIcon } from '@eti/assets/icons/revolut/exclamation-mark.svg';
import { ReactComponent as RevolutInfoIcon } from '@eti/assets/icons/revolut/info.svg';
import { useRevolut, useTranslation } from '@eti/providers';
import { css, cx, resetButtonStyle } from '@eti/styles';
import { CheckCircle, Info, WarningCircle, X } from '@phosphor-icons/react';
import variants from '../../utils/variants';
import { getBackgroundColor, getBorderColor } from './notificationStyles';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  signal
} = vars.colors;
const iconStyles = css`
  flex-shrink: 0;
  height: 1.25rem;
  width: 1.25rem;
`;
const iconProps = {
  'aria-hidden': true,
  className: iconStyles,
  weight: 'bold'
};
const revoluticonStyles = css`
  flex-shrink: 0;
  height: 1.5rem;
  width: 1.5rem;
`;
const revolutIconProps = {
  'aria-hidden': true,
  className: revoluticonStyles
};
const getIcon = (isRevolut, variant) => {
  switch (variant) {
    case variants.CONFIRMATION:
      return isRevolut ? /*#__PURE__*/_jsx(RevolutCheckIcon, {
        fill: signal.positive.foreground,
        ...revolutIconProps
      }) : /*#__PURE__*/_jsx(CheckCircle, {
        color: signal.positive.foreground,
        ...iconProps
      });
    case variants.WARNING:
      return isRevolut ? /*#__PURE__*/_jsx(RevolutWarningIcon, {
        fill: signal.caution.foreground,
        ...revolutIconProps
      }) : /*#__PURE__*/_jsx(WarningCircle, {
        color: signal.caution.foreground,
        ...iconProps
      });
    case variants.CRITICAL:
      return isRevolut ? /*#__PURE__*/_jsx(RevolutErrorIcon, {
        fill: signal.critical.foreground,
        ...revolutIconProps
      }) : /*#__PURE__*/_jsx(WarningCircle, {
        color: signal.critical.foreground,
        ...iconProps
      });
    case variants.INFORMATION:
    default:
      return isRevolut ? /*#__PURE__*/_jsx(RevolutInfoIcon, {
        fill: signal.information.foreground,
        ...revolutIconProps
      }) : /*#__PURE__*/_jsx(Info, {
        color: signal.information.foreground,
        ...iconProps
      });
  }
};
const wrapperStyles = css`
  border-radius: ${vars.card.borderRadius};
  padding: 1rem;
  padding-inline-end: 2rem;
  position: relative;
`;
const titleStyles = css`
  line-height: 1.3;
`;
const contentStyles = css`
  font-size: 0.875rem;

  & h2 {
    font-size: 1rem;
    font-weight: 700;
  }
`;
const closeButtonStyles = css`
  height: auto;
  padding: 5px;
  position: absolute;
  right: 3px;
  top: 3px;

  [dir='rtl'] & {
    left: 3px;
    right: unset;
  }

  & svg {
    height: 20px;
    width: 20px;
  }

  & span {
    display: flex;
  }

  @media (min-width: ${breakpoints._970}) {
    height: auto;
  }
`;
const Notification = /*#__PURE__*/forwardRef(({
  children,
  className,
  closeButtonDataTestId,
  closeButtonLabel = '',
  contentId,
  dataTestId,
  iconComponent,
  onClose,
  title,
  variant
}, ref) => {
  const {
    t
  } = useTranslation();
  const {
    isRevolut
  } = useRevolut();
  const closeButtonLabelText = closeButtonLabel || t('Close');
  const getAriaLabel = notificationStatus => {
    switch (notificationStatus) {
      case variants.CONFIRMATION:
        return t('ScreenReader.Notification.ConfirmationStatusMessage.AriaLabel.Text');
      case variants.CRITICAL:
        return t('ScreenReader.Notification.CriticalStatusMessageAriaLabel.Text');
      case variants.INFORMATION:
        return t('ScreenReader.Notification.InfoStatusMessage.AriaLabel.Text');
      case variants.WARNING:
        return t('ScreenReader.Notification.WarningStatusMessage.AriaLabel.Text');
      default:
        return t('ScreenReader.Notification.InfoStatusMessage.AriaLabel.Text');
    }
  };
  return /*#__PURE__*/_jsx("div", {
    "aria-label": getAriaLabel(variant),
    className: cx('etiNotification', getBackgroundColor(variant), getBorderColor(variant, isRevolut), wrapperStyles, className),
    "data-testid": dataTestId,
    ref: ref,
    tabIndex: -1,
    children: /*#__PURE__*/_jsxs(Inline, {
      noWrap: true,
      spacing: 8,
      children: [iconComponent || getIcon(isRevolut, variant), /*#__PURE__*/_jsxs(Stack, {
        spacing: 4,
        children: [title && /*#__PURE__*/_jsx(Heading, {
          as: "h2",
          className: titleStyles,
          level: 4,
          children: title
        }), /*#__PURE__*/_jsx(Text, {
          className: contentStyles,
          id: contentId,
          children: children
        })]
      }), onClose && /*#__PURE__*/_jsx("button", {
        "aria-label": closeButtonLabelText,
        className: cx(resetButtonStyle, closeButtonStyles),
        "data-testid": closeButtonDataTestId,
        onClick: onClose,
        type: "button",
        children: /*#__PURE__*/_jsx(X, {
          color: vars.colors.text.primary,
          size: 24,
          weight: "thin"
        })
      })]
    })
  });
});
export default Notification;
//# sourceMappingURL=Notification.js.map